/**
 * InvoiceReceiptsSection — Phase 10b.2 Receipt Display on Invoice Detail
 *
 * Shows a collapsible section listing receipt files for expense items on an invoice.
 * Each expense item with receipts gets a subsection with download links.
 * Only renders when there are expense items with attached receipts.
 */
import { useState } from 'react';
import { ChevronDown, ChevronRight, Paperclip, FileText, Image, Download } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useBillableItemFiles, downloadBillableItemFile } from '@/hooks/useBillableItemFiles';
import type { BillableItemFile } from '@/hooks/useBillableItemFiles';

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

interface ExpenseItem {
  id: string;
  billable_item_id: string | null;
  description: string;
  billable_item_description?: string;
  billable_item_category?: string | null;
}

interface InvoiceReceiptsSectionProps {
  /** Expense-category invoice items that may have receipts */
  expenseItems: ExpenseItem[];
  /** Map of billableItemId -> receipt file count (from parent query) */
  receiptCounts: Record<string, number>;
}

/**
 * Sub-component: renders the file list for a single billable item.
 * Uses the useBillableItemFiles hook to fetch files on demand (when expanded).
 */
function ReceiptFileList({ billableItemId }: { billableItemId: string }) {
  const { data: files, isLoading, error } = useBillableItemFiles(billableItemId);
  const [downloadingId, setDownloadingId] = useState<string | null>(null);

  const handleDownload = async (file: BillableItemFile) => {
    setDownloadingId(file.id);
    try {
      await downloadBillableItemFile(file.id, file.file_name);
    } catch {
      // Download errors are non-critical
    } finally {
      setDownloadingId(null);
    }
  };

  const isImage = (contentType: string) => contentType.startsWith('image/');

  if (isLoading) {
    return (
      <div className="py-2 text-sm text-muted-foreground">Loading receipts...</div>
    );
  }

  if (error || !files || files.length === 0) {
    return null;
  }

  return (
    <ul className="space-y-1.5 mt-2">
      {files.map((f) => (
        <li
          key={f.id}
          className="flex items-center gap-2 rounded border border-slate-100 bg-slate-50 px-3 py-1.5 text-sm"
        >
          {isImage(f.content_type) ? (
            <Image className="h-3.5 w-3.5 shrink-0 text-slate-400" />
          ) : (
            <FileText className="h-3.5 w-3.5 shrink-0 text-slate-400" />
          )}
          <span className="flex-1 truncate text-slate-700">{f.file_name}</span>
          <span className="shrink-0 text-xs text-slate-400">{formatBytes(f.file_size)}</span>
          <Button
            variant="ghost"
            size="icon"
            className="h-6 w-6 shrink-0 text-slate-400 hover:text-blue-600"
            onClick={() => handleDownload(f)}
            disabled={downloadingId === f.id}
            title={`Download ${f.file_name}`}
            data-testid={`download-receipt-${f.id}`}
          >
            <Download className="h-3 w-3" />
          </Button>
        </li>
      ))}
    </ul>
  );
}

export function InvoiceReceiptsSection({
  expenseItems,
  receiptCounts,
}: InvoiceReceiptsSectionProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [expandedItemId, setExpandedItemId] = useState<string | null>(null);

  // Only show items that have receipts
  const itemsWithReceipts = expenseItems.filter(
    (item) => item.billable_item_id && (receiptCounts[item.billable_item_id] ?? 0) > 0
  );

  // Don't render section if no items have receipts
  if (itemsWithReceipts.length === 0) {
    return null;
  }

  const totalReceipts = itemsWithReceipts.reduce(
    (sum, item) => sum + (receiptCounts[item.billable_item_id!] ?? 0),
    0
  );

  return (
    <div className="mt-6 border rounded-lg" data-testid="invoice-receipts-section">
      {/* Header — click to toggle */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex w-full items-center gap-2 px-4 py-3 text-left hover:bg-slate-50 transition-colors rounded-lg"
        data-testid="receipts-section-toggle"
      >
        {isOpen ? (
          <ChevronDown className="h-4 w-4 text-slate-500" />
        ) : (
          <ChevronRight className="h-4 w-4 text-slate-500" />
        )}
        <Paperclip className="h-4 w-4 text-amber-600" />
        <span className="text-sm font-medium">Expense Receipts</span>
        <span className="ml-1 rounded-full bg-amber-50 px-2 py-0.5 text-xs font-medium text-amber-700">
          {totalReceipts} file{totalReceipts !== 1 ? 's' : ''}
        </span>
      </button>

      {/* Collapsible body */}
      {isOpen && (
        <div className="border-t px-4 py-3 space-y-3" data-testid="receipts-section-content">
          {itemsWithReceipts.map((item) => {
            const description = item.billable_item_description ?? item.description;
            const count = receiptCounts[item.billable_item_id!] ?? 0;
            const isExpanded = expandedItemId === item.id;

            return (
              <div key={item.id} className="space-y-1">
                <button
                  onClick={() => setExpandedItemId(isExpanded ? null : item.id)}
                  className="flex w-full items-center gap-2 text-left text-sm hover:text-blue-600 transition-colors"
                  data-testid={`receipt-item-toggle-${item.id}`}
                >
                  {isExpanded ? (
                    <ChevronDown className="h-3.5 w-3.5 text-slate-400" />
                  ) : (
                    <ChevronRight className="h-3.5 w-3.5 text-slate-400" />
                  )}
                  <span className="font-medium truncate">{description}</span>
                  <span className="shrink-0 text-xs text-muted-foreground">
                    ({count} receipt{count !== 1 ? 's' : ''})
                  </span>
                </button>

                {isExpanded && item.billable_item_id && (
                  <div className="ml-5">
                    <ReceiptFileList billableItemId={item.billable_item_id} />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
